if (!customElements.get('seo-content-search')) {
  class SeoContentSearch extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.form = this.querySelector('.seo-content-search__form');
      this.input = this.querySelector('[data-seo-content-search-input]');
      this.results = this.querySelector('[data-seo-content-search-results]');
      this.status = this.querySelector('[data-seo-content-search-status]');

      if (!this.form || !this.input || !this.results || !this.status) return;

      this.initialized = true;
      this.predictiveSearchUrl = this.dataset.predictiveSearchUrl;
      this.resultsSection = this.dataset.resultsSection;
      this.resultsLimit = this.dataset.resultsLimit || '6';
      this.loadingText = this.dataset.loadingText || 'Searching articles...';
      this.emptyText = this.dataset.emptyText || 'No matching articles found.';
      this.errorText = this.dataset.errorText || 'Suggestions are unavailable. Press Enter to search.';
      this.resultSingular = this.dataset.resultSingular || 'article found';
      this.resultPlural = this.dataset.resultPlural || 'articles found';
      this.cache = new Map();
      this.options = [];
      this.activeIndex = -1;
      this.requestSequence = 0;
      this.debounceTimer = null;
      this.statusTimer = null;
      this.abortController = null;
      this.isComposing = false;

      this.handleInput = this.handleInput.bind(this);
      this.handleFocus = this.handleFocus.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleCompositionStart = this.handleCompositionStart.bind(this);
      this.handleCompositionEnd = this.handleCompositionEnd.bind(this);
      this.handleFormSubmit = this.handleFormSubmit.bind(this);
      this.handleDocumentPointerDown = this.handleDocumentPointerDown.bind(this);
      this.handleResultsPointerMove = this.handleResultsPointerMove.bind(this);

      this.input.addEventListener('input', this.handleInput);
      this.input.addEventListener('focus', this.handleFocus);
      this.input.addEventListener('keydown', this.handleKeydown);
      this.input.addEventListener('compositionstart', this.handleCompositionStart);
      this.input.addEventListener('compositionend', this.handleCompositionEnd);
      this.form.addEventListener('submit', this.handleFormSubmit);
      this.addEventListener('focusout', this.handleFocusOut);
      this.results.addEventListener('pointermove', this.handleResultsPointerMove);
      document.addEventListener('pointerdown', this.handleDocumentPointerDown);
    }

    disconnectedCallback() {
      if (!this.initialized) return;

      this.cancelPendingRequest();
      this.close({ clear: true });
      this.input.removeEventListener('input', this.handleInput);
      this.input.removeEventListener('focus', this.handleFocus);
      this.input.removeEventListener('keydown', this.handleKeydown);
      this.input.removeEventListener('compositionstart', this.handleCompositionStart);
      this.input.removeEventListener('compositionend', this.handleCompositionEnd);
      this.form.removeEventListener('submit', this.handleFormSubmit);
      this.removeEventListener('focusout', this.handleFocusOut);
      this.results.removeEventListener('pointermove', this.handleResultsPointerMove);
      document.removeEventListener('pointerdown', this.handleDocumentPointerDown);
      this.initialized = false;
    }

    getQuery() {
      return this.input.value.trim();
    }

    getCacheKey(query) {
      return query.toLocaleLowerCase();
    }

    handleInput() {
      if (this.isComposing) return;

      const query = this.getQuery();
      this.close({ abort: true, clear: query.length === 0 });

      if (query.length > 0) this.scheduleSearch(query);
    }

    handleFocus() {
      const query = this.getQuery();
      if (!query.length) return;

      const cachedMarkup = this.cache.get(this.getCacheKey(query));
      if (cachedMarkup) {
        this.renderMarkup(cachedMarkup);
      } else {
        this.scheduleSearch(query, 0);
      }
    }

    handleFocusOut() {
      window.setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close({ abort: true });
      }, 0);
    }

    handleCompositionStart() {
      this.isComposing = true;
      this.close({ abort: true });
    }

    handleCompositionEnd() {
      this.isComposing = false;
      this.handleInput();
    }

    handleFormSubmit(event) {
      if (!this.getQuery().length) event.preventDefault();
    }

    handleDocumentPointerDown(event) {
      if (!this.contains(event.target)) this.close({ abort: true });
    }

    handleResultsPointerMove(event) {
      const option = event.target.closest('[data-seo-content-search-option]');
      if (!option || !this.results.contains(option)) return;

      const index = this.options.indexOf(option);
      if (index >= 0) this.selectOption(index, false);
    }

    handleKeydown(event) {
      if (this.isComposing) return;

      if (event.key === 'Escape' && this.isOpen()) {
        event.preventDefault();
        this.close({ abort: true });
        return;
      }

      if (event.key === 'Tab') {
        this.close({ abort: true });
        return;
      }

      if (event.key === 'Enter' && this.activeIndex >= 0) {
        const activeOption = this.options[this.activeIndex];
        if (activeOption) {
          event.preventDefault();
          activeOption.click();
        }
        return;
      }

      if (
        (event.key === 'Home' || event.key === 'End')
        && this.isOpen()
        && this.options.length
        && this.activeIndex >= 0
      ) {
        event.preventDefault();
        this.selectOption(event.key === 'Home' ? 0 : this.options.length - 1, true);
        return;
      }

      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      const query = this.getQuery();
      if (!query.length) return;

      event.preventDefault();

      if (!this.isOpen()) {
        const cachedMarkup = this.cache.get(this.getCacheKey(query));
        if (cachedMarkup) {
          this.renderMarkup(cachedMarkup);
        } else {
          this.scheduleSearch(query, 0);
          return;
        }
      }

      if (!this.options.length) return;

      if (event.key === 'ArrowDown') {
        this.selectOption(this.activeIndex + 1, true);
      } else {
        this.selectOption(this.activeIndex <= 0 ? this.options.length - 1 : this.activeIndex - 1, true);
      }
    }

    scheduleSearch(query, delay = 275) {
      window.clearTimeout(this.debounceTimer);
      this.debounceTimer = window.setTimeout(() => this.loadResults(query), delay);
    }

    async loadResults(query) {
      if (query !== this.getQuery()) return;

      const cacheKey = this.getCacheKey(query);
      const cachedMarkup = this.cache.get(cacheKey);
      if (cachedMarkup) {
        this.renderMarkup(cachedMarkup);
        return;
      }

      this.cancelPendingRequest();
      const requestSequence = ++this.requestSequence;
      this.abortController = new AbortController();
      this.showMessage(this.loadingText, 'is-loading');
      this.results.setAttribute('aria-busy', 'true');
      this.setStatus(this.loadingText);

      try {
        const requestUrl = new URL(this.predictiveSearchUrl, window.location.origin);
        requestUrl.searchParams.set('q', query);
        requestUrl.searchParams.set('resources[type]', 'article');
        requestUrl.searchParams.set('resources[limit]', this.resultsLimit);
        requestUrl.searchParams.set('resources[limit_scope]', 'all');
        requestUrl.searchParams.set('resources[options][fields]', 'title,body,tag,author');
        requestUrl.searchParams.set('section_id', this.resultsSection);

        const response = await fetch(requestUrl.toString(), {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
          signal: this.abortController.signal
        });

        if (!response.ok) throw new Error(`Predictive search request failed: ${response.status}`);

        const responseText = await response.text();
        const responseDocument = new DOMParser().parseFromString(responseText, 'text/html');
        const section = responseDocument.getElementById(`shopify-section-${this.resultsSection}`);
        const responseContent = section?.querySelector('[data-seo-content-search-response]');

        if (!responseContent) throw new Error('Predictive search response is missing its results section.');
        if (requestSequence !== this.requestSequence || query !== this.getQuery()) return;

        const markup = responseContent.innerHTML.trim();
        this.cache.set(cacheKey, markup);
        if (this.cache.size > 20) this.cache.delete(this.cache.keys().next().value);
        this.renderMarkup(markup);
      } catch (error) {
        if (error.name === 'AbortError') return;
        if (requestSequence !== this.requestSequence || query !== this.getQuery()) return;

        this.showMessage(this.errorText, 'is-error');
        this.setStatus(this.errorText);
      } finally {
        if (requestSequence === this.requestSequence) {
          this.results.setAttribute('aria-busy', 'false');
          this.abortController = null;
        }
      }
    }

    renderMarkup(markup) {
      const template = document.createElement('template');
      template.innerHTML = markup;
      this.clearSelection();
      this.results.replaceChildren(template.content.cloneNode(true));
      this.options = Array.from(this.results.querySelectorAll('[data-seo-content-search-option]'));
      this.activeIndex = -1;

      this.options.forEach((option, index) => {
        option.id = `${this.input.id}-option-${index}`;
        option.setAttribute('aria-selected', 'false');
      });

      this.results.setAttribute('aria-busy', 'false');
      const resultCount = this.options.length;
      if (resultCount > 0) {
        this.results.setAttribute('role', 'listbox');
        this.open(true);
      } else {
        this.results.removeAttribute('role');
        this.open(false);
      }

      this.setStatus(
        resultCount > 0
          ? `${resultCount} ${resultCount === 1 ? this.resultSingular : this.resultPlural}`
          : this.emptyText
      );
    }

    showMessage(message, modifierClass) {
      const messageElement = document.createElement('p');
      messageElement.className = `seo-content-search__state ${modifierClass}`;
      messageElement.textContent = message;
      this.clearSelection();
      this.results.replaceChildren(messageElement);
      this.options = [];
      this.activeIndex = -1;
      this.results.removeAttribute('role');
      this.open(false);
    }

    selectOption(index, announce) {
      if (!this.options.length) return;

      const normalizedIndex = (index + this.options.length) % this.options.length;
      if (normalizedIndex === this.activeIndex) return;

      if (this.activeIndex >= 0) {
        this.options[this.activeIndex].setAttribute('aria-selected', 'false');
      }

      this.activeIndex = normalizedIndex;
      const activeOption = this.options[this.activeIndex];
      activeOption.setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', activeOption.id);
      this.keepOptionVisible(activeOption);

      if (announce) this.setStatus(activeOption.textContent.trim());
    }

    keepOptionVisible(option) {
      const resultsRect = this.results.getBoundingClientRect();
      const optionRect = option.getBoundingClientRect();

      if (optionRect.top < resultsRect.top) {
        this.results.scrollTop -= resultsRect.top - optionRect.top;
      } else if (optionRect.bottom > resultsRect.bottom) {
        this.results.scrollTop += optionRect.bottom - resultsRect.bottom;
      }
    }

    clearSelection() {
      if (this.activeIndex >= 0 && this.options[this.activeIndex]) {
        this.options[this.activeIndex].setAttribute('aria-selected', 'false');
      }

      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
    }

    open(expanded = true) {
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    isOpen() {
      return !this.results.hidden;
    }

    close({ abort = false, clear = false } = {}) {
      if (abort) this.cancelPendingRequest();
      this.clearSelection();
      this.results.hidden = true;
      this.results.setAttribute('aria-busy', 'false');
      this.results.removeAttribute('role');
      this.input.setAttribute('aria-expanded', 'false');
      this.clearStatus();
      if (clear) {
        this.results.replaceChildren();
        this.options = [];
      }
    }

    cancelPendingRequest() {
      window.clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
      this.requestSequence += 1;

      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
    }

    setStatus(message) {
      this.clearStatus();
      if (!message) return;

      this.statusTimer = window.setTimeout(() => {
        if (this.status) this.status.textContent = message;
      }, 20);
    }

    clearStatus() {
      window.clearTimeout(this.statusTimer);
      this.statusTimer = null;
      if (this.status) this.status.textContent = '';
    }
  }

  customElements.define('seo-content-search', SeoContentSearch);
}
